// packages/permissions-ui/src/deny.ts
//
// ENG-62 — turns a Cedar/RBAC denial into a sentence a user can act on.
//
// Backend contract (services/api/src/cedar_check.ts, ENG-61): a denied
// permission check throws `ForbiddenError`, a GraphQLError whose `message`
// is a debug string ("Forbidden: clearpath.steps.assign on StepRun
// (rbac_miss)") and whose `extensions` carry the same information as
// structured fields:
//
//   { code: 'Forbidden', action: string, resourceType: string, reason: CedarDenyReason }
//
// `CedarDenyReason` is one of 'rbac_miss' | 'out_of_scope' | 'avp_deny' |
// 'feature_flag_disabled' (mirrored below — services/api is a backend
// Lambda package, not something a browser bundle can import from).
//
// `denyMessage` maps { action, reason } to a plain sentence. `denyMessageFromError`
// duck-types an unknown thrown/rejected value (ApolloError, GraphQLError, or
// a plain object) to find those extensions without this package depending on
// @apollo/client or graphql — every app here already has its own copy of
// those, and this package stays a pure-function leaf with no framework
// dependency.
//
// P0-2 — `denyMessageFromError` also recognizes `StepUpRequired`
// (services/api/src/auth/require_recent_mfa.ts: `{ code: 'StepUpRequired',
// action, maxAgeMinutes, reason }`), a freshness gate distinct from
// Forbidden's permission taxonomy. Callers that own a full re-verify+retry
// flow (apps/main's useMutationWithStepUp) handle that error themselves and
// don't need this; this is the fallback for any OTHER caller that just
// surfaces mutation errors generically and would otherwise show nothing (or
// a raw GraphQL message) for a step-up-gated action.
//
// ENG-2539 — the candidate walk is split into two composed halves so a
// second caller (the shared `failureCopy` ladder in ./failure-copy.ts) can
// apply an audience-narrowing predicate to the SAME matched candidate the
// sentence is built from, rather than re-walking `graphQLErrors` on its own
// and risking a predicate that describes a different candidate than the one
// the sentence came from. `matchedDenyExtensions` is the walk (which
// candidate, if any, this module would act on); `denyMessageFromExtensions`
// is the sentence for an already-matched candidate. `denyMessageFromError`
// is exactly those two composed, so its signature, its 91 call sites, and
// every case in deny.test.ts are unchanged.
//
// Both new functions are exported from THIS module for `failure-copy.ts` to
// import directly (same package, sibling file) but are deliberately NOT
// re-exported from `./index.ts`, the package's public barrel: an app outside
// this package should never receive a raw denial-extensions object to
// inspect or render — only a vetted sentence (`denyMessage`,
// `denyMessageFromError`, or `failureCopy`). See the barrel's own comment.

import { BUDGET_EXCEEDED_REASON, budgetExceededMessage } from '@clearpath/shared-types/errors';

import { permissionLabel } from './labels.js';

/** Mirrors services/api/src/cedar_check.ts `CedarDenyReason` (ENG-61). */
export type CedarDenyReason = 'rbac_miss' | 'out_of_scope' | 'avp_deny' | 'feature_flag_disabled';

export interface DenyMessageInput {
  /** The permission key the check was for, e.g. "clearpath.steps.assign". */
  readonly action?: string | null;
  /** Which layer denied, per ForbiddenError.extensions.reason. */
  readonly reason?: string | null;
  /** Support correlation id — appended to the sentence when present so a
   *  user-reported screenshot can be matched to server-side logs. */
  readonly correlationId?: string | null;
  /**
   * The SPECIFIC permission a `self_grant_denied` denial refused to grant
   * (services/api/src/handlers/roles.ts SelfEscalationError). Distinct from
   * `action` (the roles.update/roles.create action being performed, which
   * the caller DOES hold — the denial is about the permission being handed
   * to a role, not the caller's ability to edit roles at all).
   */
  readonly permissionKey?: string | null;
  /**
   * `extensions.cause` of a `budget_exceeded` denial (ENG-3054 D7): `limit`,
   * `single_change_too_large` or `unreadable`; it picks the sentence. Ignored
   * for every other reason.
   */
  readonly cause?: string | null;
  /**
   * `extensions.retryable` of a `budget_exceeded` denial (ENG-3054 D4): the
   * fallback when `cause` is missing or unknown (`false` gets the neutral
   * contact-support sentence, D8; anything else the limit sentence). Ignored
   * for every other reason.
   */
  readonly retryable?: boolean | null;
}

function lowerFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/**
 * Human sentence for a denied permission check. Never mentions the raw
 * permission key, the Cedar/RBAC reason code, or "Forbidden" — those are
 * implementation detail, not something a lab tech, dentist, or admin should
 * have to parse.
 */
export function denyMessage(input: DenyMessageInput): string {
  const label = input.action ? lowerFirst(permissionLabel(input.action)) : 'do this';

  let sentence: string;
  switch (input.reason) {
    case 'rbac_miss':
      sentence = `You don't have permission to ${label}. Ask your admin for access.`;
      break;
    case 'out_of_scope':
      sentence = 'This item belongs to a team outside your scope.';
      break;
    case 'avp_deny':
      // A real (ABAC/Cedar) policy evaluated to deny — distinct from a plain
      // role gap: the user may hold the base permission but not for THIS
      // specific item (its current status, ownership, etc).
      sentence = `You don't have permission to ${label} for this item.`;
      break;
    case 'feature_flag_disabled':
      sentence = "This feature isn't enabled for your organization.";
      break;
    case 'lifecycle_locked':
      // NOT a permission gap — a published/immutable lifecycle state. The
      // server rejects an in-place edit of a LIVE form_definition / pipeline
      // (assertLifecycleEditable / assertPipelineDraftInOrg) with a Forbidden
      // that carries `lifecycleStatus`/`pipelineStatus` but NO action/reason.
      // Without this case such errors fell through to the generic "you don't
      // have permission" below — which is what made a Lab Owner with full
      // rights think editing a published form was an access problem.
      sentence =
        "This item is already published, so it can't be edited directly. " +
        'Open it to start a new version (your changes go live when you save), ' +
        'or create a new one.';
      break;
    case BUDGET_EXCEEDED_REASON:
      // NOT a permission gap — ENG-3054 AD-6: the organization's rolling 24h
      // limit on customers losing the ability to order
      // (services/api/src/catalog/entitlements/budget.ts). The server's
      // Forbidden carries `reason` but no `action`; without this case it fell
      // through to "You don't have permission". The sentence is shared with
      // the server, picked by `cause` (D7: "try again later" only for
      // `limit`; for a missing or unknown cause, `retryable: false` gets the
      // neutral D8 sentence, anything else the limit one), and shows no numbers.
      sentence = budgetExceededMessage(input.cause, input.retryable);
      break;
    case 'self_grant_denied': {
      // Standard RBAC delegation rule: you cannot grant a permission you
      // don't yourself hold (services/api/src/handlers/roles.ts
      // SelfEscalationError). Name the SPECIFIC permission being refused,
      // not the roles.update/roles.create action — the caller does hold
      // that.
      const grantedLabel = input.permissionKey ? lowerFirst(permissionLabel(input.permissionKey)) : 'that permission';
      sentence = `You can't grant "${grantedLabel}" because you don't have it yourself.`;
      break;
    }
    default:
      sentence = `You don't have permission to ${label}.`;
  }

  return input.correlationId ? `${sentence} (Reference: ${input.correlationId})` : sentence;
}

/** Structural shape of `ForbiddenError.extensions` — see cedar_check.ts
 *  (and handlers/roles.ts's SelfEscalationError, which adds permissionKey).
 *  Exported so `failure-copy.ts` can type the matched candidate it narrows
 *  on; NOT re-exported from `./index.ts` (see the module docblock above). */
export interface DenyExtensions {
  readonly code?: unknown;
  readonly action?: unknown;
  readonly resourceType?: unknown;
  readonly reason?: unknown;
  readonly permissionKey?: unknown;
  /** Set on a `budget_exceeded` Forbidden (ENG-3054 D7). */
  readonly cause?: unknown;
  /** Set on a `budget_exceeded` Forbidden (ENG-3054 D4). */
  readonly retryable?: unknown;
  /** Set by lifecycle guards (assertLifecycleEditable / assertPipelineDraftInOrg)
   *  on a Forbidden that is a published-state block, not a permission gap. */
  readonly lifecycleStatus?: unknown;
  readonly pipelineStatus?: unknown;
}

/** True when a Forbidden is a lifecycle/immutability block (a published LIVE
 *  form_definition or pipeline) rather than a Cedar/RBAC permission denial. */
export function isLifecycleForbidden(ext: {
  readonly lifecycleStatus?: unknown;
  readonly pipelineStatus?: unknown;
}): boolean {
  return typeof ext.lifecycleStatus === 'string' || typeof ext.pipelineStatus === 'string';
}

function extensionsOf(err: unknown): DenyExtensions | null {
  if (!err || typeof err !== 'object') return null;
  // GraphQLError-shaped: { extensions: {...} }
  const direct = (err as { extensions?: unknown }).extensions;
  if (direct && typeof direct === 'object') return direct as DenyExtensions;
  return null;
}

/**
 * Human sentence for a `StepUpRequired` error (services/api/src/auth/
 * require_recent_mfa.ts) — the caller's last verified-MFA timestamp is too
 * old for this action. A freshness gate, not a permission gate, so it does
 * not go through `denyMessage`'s Forbidden-reason taxonomy.
 */
export function stepUpRequiredMessage(action?: string | null): string {
  const label = action ? lowerFirst(permissionLabel(action)) : 'do this';
  return `Verify your identity to ${label} — this action needs a fresh security check.`;
}

/**
 * The walk: duck-types `err` as an ApolloError / GraphQLError-ish value and
 * returns the `extensions` of the FIRST candidate that carries a `Forbidden`
 * or `StepUpRequired` code, or `null` when none does (a network error, a
 * validation error, some other GraphQL error code, a plain string, ...).
 *
 * Accepts either:
 *   - an ApolloError-like object with a `graphQLErrors` array, or
 *   - a single GraphQLError-like object with `extensions` directly.
 *
 * This is the ONLY place that walks `graphQLErrors` looking for a deny
 * candidate — `denyMessageFromError` below and `failureCopy`
 * (./failure-copy.ts) both compose on top of it, so a predicate evaluated on
 * its result is guaranteed to describe the same candidate a sentence would
 * be built from.
 */
export function matchedDenyExtensions(err: unknown): DenyExtensions | null {
  if (!err || typeof err !== 'object') return null;

  const graphQLErrors = (err as { errors?: unknown }).errors ??
    (err as { graphQLErrors?: unknown }).graphQLErrors;
  const candidates: unknown[] = Array.isArray(graphQLErrors) ? graphQLErrors : [err];

  for (const candidate of candidates) {
    const ext = extensionsOf(candidate);
    if (!ext) continue;
    if (ext.code === 'StepUpRequired' || ext.code === 'Forbidden' || ext.code === 'FORBIDDEN') {
      return ext;
    }
  }
  return null;
}

/**
 * The sentence for an already-matched candidate (see `matchedDenyExtensions`
 * above) — a `Forbidden` or `StepUpRequired` extensions object, never
 * anything else. Not defensive about a candidate that doesn't carry one of
 * those two codes: callers are expected to pass exactly what the walk
 * returned.
 */
export function denyMessageFromExtensions(
  ext: DenyExtensions,
  correlationId?: string | null,
): string | null {
  if (ext.code === 'StepUpRequired') {
    const action = typeof ext.action === 'string' ? ext.action : null;
    const sentence = stepUpRequiredMessage(action);
    return correlationId ? `${sentence} (Reference: ${correlationId})` : sentence;
  }
  if (ext.code !== 'Forbidden' && ext.code !== 'FORBIDDEN') return null;
  // A lifecycle/immutability Forbidden is NOT a permission denial — give the
  // "publish/version" sentence rather than "you don't have permission".
  if (isLifecycleForbidden(ext)) {
    return denyMessage({ reason: 'lifecycle_locked', correlationId });
  }
  const action = typeof ext.action === 'string' ? ext.action : null;
  const reason = typeof ext.reason === 'string' ? ext.reason : null;
  const permissionKey = typeof ext.permissionKey === 'string' ? ext.permissionKey : null;
  const cause = typeof ext.cause === 'string' ? ext.cause : null;
  const retryable = typeof ext.retryable === 'boolean' ? ext.retryable : null;
  return denyMessage({ action, reason, correlationId, permissionKey, cause, retryable });
}

/**
 * Duck-types `err` as an ApolloError / GraphQLError-ish value and, if its
 * first GraphQL error carries a `Forbidden` or `StepUpRequired` extensions
 * payload, returns a humanized sentence. Returns `null` for anything else
 * (network errors, validation errors, other GraphQL errors, plain strings)
 * so callers can fall back to their own generic handling.
 *
 * Accepts either:
 *   - an ApolloError-like object with a `graphQLErrors` array, or
 *   - a single GraphQLError-like object with `extensions` directly.
 *
 * `matchedDenyExtensions` + `denyMessageFromExtensions` composed — this
 * signature and every case in deny.test.ts are unchanged by that split.
 */
export function denyMessageFromError(err: unknown, correlationId?: string | null): string | null {
  const ext = matchedDenyExtensions(err);
  return ext ? denyMessageFromExtensions(ext, correlationId) : null;
}
